import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Phone } from "lucide-react";
import { useApp } from "@/state/AppContext";
import { usePageTitle } from "@/lib/usePageTitle";
import { api, apiErrorMessage, rows } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { addressLabel } from "@/lib/addressLabel";
import { telHref, validatePhone } from "@/lib/phone";

const OPEN_STATUSES = ["requested", "accepted", "dispatched", "arriving", "arrived", "in_service"];

const AMBULANCE_KIND_LABELS = {
  basic: { en: "Basic", hi: "बेसिक" },
  oxygen: { en: "Oxygen", hi: "ऑक्सीजन" },
  icu: { en: "ICU", hi: "आईसीयू" },
  mortuary: { en: "Mortuary", hi: "शवयान" },
};

/** Citizen ambulance request — the website counterpart to mobile's ambulance/index.tsx. Call 108
 * is always the first control, reachable signed out, matching FEATURES.md's "never buried". */
export default function Ambulance() {
  const { t, lang, tenant, auth } = useApp();
  usePageTitle(t("ambulance.title")); // RF-21: per-page tab title
  const navigate = useNavigate();
  const [config, setConfig] = useState(null);
  const [live, setLive] = useState(null);
  const [type, setType] = useState("basic");
  const [pickup, setPickup] = useState("");
  const [landmark, setLandmark] = useState("");
  const [pickedGeo, setPickedGeo] = useState(null);
  const [destination, setDestination] = useState("");
  const [phone, setPhone] = useState("");
  const [note, setNote] = useState("");
  const [providerId, setProviderId] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [savedAddresses, setSavedAddresses] = useState([]);

  useEffect(() => {
    if (!tenant) return;
    api.get("/ambulance/config", { params: { tenant_id: tenant.id } }).then((r) => setConfig(r.data));
  }, [tenant]);

  useEffect(() => {
    if (!auth) return;
    api.get("/addresses").then((r) => setSavedAddresses(rows(r.data))).catch(() => undefined);
  }, [auth]);

  useEffect(() => {
    if (!auth) return;
    api.get("/ambulance/bookings/mine").then((r) => {
      setLive(r.data.find((b) => OPEN_STATUSES.includes(b.status)) || null);
    });
  }, [auth]);

  useEffect(() => {
    if (auth?.identity?.phone_e164) setPhone(auth.identity.phone_e164.replace("+91", ""));
  }, [auth]);

  const submit = async () => {
    setError("");
    setSubmitting(true);
    try {
      const { data } = await api.post("/ambulance/bookings", {
        // R11 PHONE: the validated value, never `replace(/\D/g, "")` (which dropped letters silently).
        contact_phone: validatePhone(phone, "mobile").value,
        pickup_address: pickup.trim(),
        pickup_landmark: landmark.trim(),
        pickup_lat: pickedGeo?.lat,
        pickup_lng: pickedGeo?.lng,
        destination: destination.trim(),
        ambulance_kind: type,
        emergency_note: note.trim(),
        provider_id: providerId || null,
      });
      navigate(`/ambulance/${data.id}`);
    } catch (e) {
      setError(apiErrorMessage(e, t("errors.generic")));
    } finally {
      setSubmitting(false);
    }
  };

  const phoneCheck = validatePhone(phone, "mobile");
  const canRequest = pickup.trim().length > 2 && phoneCheck.ok && phoneCheck.value !== "";
  const providers = config?.providers ?? [];
  const emergencyNumber = config?.emergency_numbers?.[0]?.number ?? "108";
  const number112 = config?.emergency_numbers?.[1]?.number ?? "112";

  return (
    <div className="mx-auto max-w-2xl px-5 py-12 sm:px-8" data-testid="ambulance-page">
      <div className="rounded-2xl bg-destructive p-6 text-destructive-foreground sm:p-8">
        <h1 className="font-heading text-3xl font-bold tracking-tight">{t("ambulance.title")}</h1>
        <p className="mt-2 text-sm text-white/90">{t("ambulance.subtitle")}</p>
        {/* The FIRST and most prominent action, above the booking form: one-tap dial 108 or 112,
            reachable signed out — a government line reaches help even when no in-app provider can. */}
        <p className="mt-4 text-base font-extrabold" data-testid="ambulance-emergency-heading">{t("ambulance.emergencyCallNow")}</p>
        <div className="mt-3 flex gap-3">
          <a
            href={telHref(emergencyNumber) || "tel:108"}
            data-testid="ambulance-call-108-btn"
            className="flex min-h-[52px] flex-1 items-center justify-center gap-2 rounded-full bg-white px-5 font-bold text-destructive"
          >
            <Phone size={17} /> {t("ambulance.call108")}
          </a>
          <a
            href={telHref(number112) || "tel:112"}
            data-testid="ambulance-call-112-btn"
            className="flex min-h-[52px] flex-1 items-center justify-center gap-2 rounded-full bg-white px-5 font-bold text-destructive"
          >
            <Phone size={17} /> {t("ambulance.call112")}
          </a>
        </div>
        <p className="mt-3 text-center text-xs text-white/80">{t("ambulance.call108Hint")}</p>
      </div>

      <div className="mt-8">
        {live ? (
          <div className="space-y-4 rounded-2xl border border-border p-6" data-testid="ambulance-live-banner">
            <p className="font-semibold">{t("ambulance.liveTitle")}</p>
            <p className="text-sm text-muted-foreground">{t(`ambulance.statusLabels.${live.status}`)}</p>
            <Button className="rounded-full" onClick={() => navigate(`/ambulance/${live.id}`)} data-testid="ambulance-live-open-btn">
              {t("ambulance.liveOpen")}
            </Button>
          </div>
        ) : !config ? (
          <p className="text-center text-muted-foreground">{t("common.loading")}</p>
        ) : !config.enabled ? (
          <div
            className="rounded-2xl border border-dashed border-border p-10 text-center text-muted-foreground"
            data-testid="ambulance-no-provider"
          >
            <p>{t("ambulance.noProvider")}</p>
            <p className="mt-1 text-sm">{t("ambulance.noProviderHint")}</p>
          </div>
        ) : !auth ? (
          <div className="space-y-4 rounded-2xl border border-border p-6" data-testid="ambulance-signin-card">
            <p>{t("ambulance.signInNeeded")}</p>
            <Button className="rounded-full" onClick={() => navigate("/login")} data-testid="ambulance-sign-in-btn">
              {t("common.signIn")}
            </Button>
          </div>
        ) : (
          <div className="space-y-5" data-testid="ambulance-form">
            <div>
              <Label>{t("ambulance.type")}</Label>
              <div className="mt-2 flex flex-wrap gap-2" data-testid="ambulance-type-chips">
                {(config.kinds ?? []).map((kind) => (
                  <button
                    key={kind}
                    type="button"
                    onClick={() => setType(kind)}
                    data-testid={`ambulance-type-${kind}`}
                    className={`rounded-full border px-4 py-1.5 text-sm transition-colors ${
                      type === kind ? "border-transparent bg-[hsl(var(--primary))] text-primary-foreground" : "border-border"
                    }`}
                  >
                    {AMBULANCE_KIND_LABELS[kind]?.[lang === "hi" ? "hi" : "en"] ?? kind}
                  </button>
                ))}
              </div>
            </div>

            <div className="space-y-1.5">
              {savedAddresses.length > 0 && (
                <div className="flex flex-wrap gap-2" data-testid="ambulance-saved-address-picker">
                  {savedAddresses.map((item) => (
                    <button
                      key={item.id}
                      type="button"
                      data-testid={`ambulance-saved-address-${item.id}`}
                      onClick={() => {
                        setPickup(item.address);
                        setLandmark(item.landmark);
                        setPickedGeo(
                          typeof item.geo_lat === "number" && typeof item.geo_lng === "number"
                            ? { lat: item.geo_lat, lng: item.geo_lng }
                            : null,
                        );
                      }}
                      className="rounded-full border border-border px-3 py-1 text-xs"
                    >
                      {addressLabel(item.label, t)}
                    </button>
                  ))}
                </div>
              )}
              <Label htmlFor="ambulance-pickup">{t("ambulance.pickup")}</Label>
              <Input
                id="ambulance-pickup"
                data-testid="ambulance-pickup-input"
                value={pickup}
                onChange={(e) => {
                  setPickup(e.target.value);
                  setPickedGeo(null);
                }}
                placeholder={t("ambulance.pickupPlaceholder")}
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="ambulance-landmark">{t("ambulance.landmark")}</Label>
              <Input
                id="ambulance-landmark"
                data-testid="ambulance-landmark-input"
                value={landmark}
                onChange={(e) => setLandmark(e.target.value)}
                placeholder={t("ambulance.landmarkPlaceholder")}
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="ambulance-destination">
                {t("ambulance.destination")} ({t("common.optional")})
              </Label>
              <Input
                id="ambulance-destination"
                data-testid="ambulance-destination-input"
                value={destination}
                onChange={(e) => setDestination(e.target.value)}
                placeholder={t("ambulance.destinationPlaceholder")}
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="ambulance-phone">{t("ambulance.contact")}</Label>
              <Input
                id="ambulance-phone"
                data-testid="ambulance-phone-input"
                value={phone}
                onChange={(e) => setPhone(e.target.value.replace(/\D/g, ""))}
                maxLength={10}
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="ambulance-note">
                {t("ambulance.note")} ({t("common.optional")})
              </Label>
              <Textarea
                id="ambulance-note"
                data-testid="ambulance-note-input"
                rows={3}
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder={t("ambulance.notePlaceholder")}
              />
            </div>

            <div>
              <Label>{t("ambulance.provider")}</Label>
              <div className="mt-2 flex flex-wrap gap-2" data-testid="ambulance-provider-chips">
                <button
                  type="button"
                  onClick={() => setProviderId("")}
                  data-testid="ambulance-provider-any"
                  className={`rounded-full border px-4 py-1.5 text-sm transition-colors ${
                    !providerId ? "border-transparent bg-[hsl(var(--primary))] text-primary-foreground" : "border-border"
                  }`}
                >
                  {t("ambulance.anyProvider")}
                </button>
                {providers.map((provider) => (
                  <button
                    key={provider.id}
                    type="button"
                    onClick={() => setProviderId(provider.id)}
                    data-testid={`ambulance-provider-${provider.id}`}
                    className={`rounded-full border px-4 py-1.5 text-sm transition-colors ${
                      providerId === provider.id ? "border-transparent bg-[hsl(var(--primary))] text-primary-foreground" : "border-border"
                    } ${provider.effective_on_duty ? "" : "opacity-60"}`}
                  >
                    {lang === "hi" ? provider.name_hi || provider.name : provider.name}
                    {provider.rating_count ? ` · ${provider.rating_avg.toFixed(1)}★` : ""}
                    <span
                      className={`ml-1.5 inline-flex items-center gap-1 text-xs ${
                        providerId === provider.id ? "text-primary-foreground/80" : "text-muted-foreground"
                      }`}
                      data-testid={`ambulance-provider-duty-${provider.id}`}
                    >
                      <span
                        className={`inline-block h-1.5 w-1.5 rounded-full ${provider.effective_on_duty ? "bg-emerald-500" : "bg-muted-foreground"}`}
                        aria-hidden="true"
                      />
                      {t(provider.effective_on_duty ? "ambulance.onDuty" : "ambulance.offDuty")}
                    </span>
                  </button>
                ))}
              </div>
              <p className="mt-2 text-xs text-muted-foreground">
                {providerId
                  ? t("ambulance.trips", { count: providers.find((p) => p.id === providerId)?.completed_trips ?? 0 })
                  : t("ambulance.anyProviderHint", { count: providers.length })}
              </p>
            </div>

            {error && (
              <p className="text-sm text-destructive" data-testid="ambulance-error">
                {error}
              </p>
            )}

            <Button
              className="w-full rounded-full bg-destructive hover:bg-destructive/90"
              disabled={!canRequest || submitting}
              onClick={submit}
              data-testid="ambulance-request-btn"
            >
              {submitting ? t("ambulance.requesting") : t("ambulance.request")}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
